import React from "react"
import styled from "styled-components"
import ContentWrapper from "../../ContentWrapper"

// Styles
import mq from "../../../styles/breakpoints"
import colors from "../../../styles/colors"
import { fonts } from "../../../styles/fonts"
import { fontWeights } from "../../../styles/textStyles"

// Components
import CaseStudyCard, { ICaseStudy } from "./CaseStudyCard"
import TitleDescriptionImage from "../../slices/TitleDescriptionImage"
import QuoteSlice from "../../slices/QuoteSlice"
import TextDescriptionColumns from "../../slices/TextDescriptionColumns"
import StackColumnsContent from "../../slices/StackColumnsContent"

interface IProps {
  pageTitle: string
  pageDescription: string
  caseStudies: ICaseStudy[]
  body?: any[]
}

const CasesPage: React.FC<IProps> = ({
  pageTitle,
  pageDescription,
  caseStudies,
  body,
}) => {
  // Group cases into rows of 2
  const rows: ICaseStudy[][] = []
  for (let i = 0; i < caseStudies.length; i += 2) {
    rows.push(caseStudies.slice(i, i + 2))
  }

  return (
    <Container>
      <StyledContentWrapper>
        <HeaderSection>
          <PageTitle>{pageTitle}</PageTitle>
          <PageDescription>{pageDescription}</PageDescription>
        </HeaderSection>

        <CasesGrid>
          {rows.map((row, rowIndex) => (
            <Row key={rowIndex}>
              {row.map((caseStudy, colIndex) => (
                <CardWrapper
                  key={caseStudy.slug}
                  isSmall={
                    row.length === 2 &&
                    ((rowIndex % 2 === 0 && colIndex === 0) ||
                      (rowIndex % 2 !== 0 && colIndex === 1))
                  }
                >
                  <CaseStudyCard caseStudy={caseStudy} />
                </CardWrapper>
              ))}
            </Row>
          ))}
        </CasesGrid>
      </StyledContentWrapper>

      {body && body.length > 0 && (
        <SlicesSection>
          {body.map((slice: any, index: number) => (
            <React.Fragment key={index}>
              {slice.slice_type === "titledescriptionimage" && (
                <TitleDescriptionImage
                  primary={{
                    align: slice.primary.align,
                    description1: slice.primary.description1.html,
                    title: slice.primary.title.raw,
                    subtitle: slice.primary.subtitle,
                    image_position: slice.primary.image_position,
                    image: slice.primary.image,
                  }}
                />
              )}
              {slice.slice_type === "quote" && (
                <QuoteSlice
                  primary={{
                    title: slice.primary.title.raw,
                    subtitle: slice.primary.subtitle,
                  }}
                />
              )}
              {slice.slice_type === "textdescriptioncolumns" && (
                <TextDescriptionColumns
                  primary={{
                    columnsPerRow: slice.primary.columns_per_row,
                  }}
                  items={slice.items.map((item: any) => ({
                    title: item.title.raw,
                    description1: item.description1.html,
                  }))}
                />
              )}
              {slice.slice_type === "stackcolumnscontent" && (
                <StackColumnsContent
                  items={slice.items.map((item: any) => ({
                    image: item.image,
                    title: item.title.raw,
                    description1: item.description1.html,
                  }))}
                />
              )}
            </React.Fragment>
          ))}
        </SlicesSection>
      )}
    </Container>
  )
}

export default CasesPage

const Container = styled.section`
  position: relative;
  padding: 96px 0 80px;
  color: ${colors.text};
  background-color: ${colors.main["900"]};

  ${mq.from.S`
    padding: 104px 0 80px;
  `}

  ${mq.from.M`
    padding: 168px 0 80px;
  `}
`

const StyledContentWrapper = styled(ContentWrapper)`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mq.from.M`
    gap: 64px;
  `}
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
`

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 48px;
  font-weight: ${fontWeights.bold};
  line-height: 60px;
  letter-spacing: -1.5px;
  color: ${colors.main["100"]};
`

const PageDescription = styled.p`
  margin: 0;
  font-family: ${fonts.primary};
  font-size: 20px;
  font-weight: ${fontWeights.regular};
  line-height: 32px;
  letter-spacing: -0.2px;
  color: ${colors.main["300"]};
`

const CasesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mq.from.M`
    gap: 48px;
  `}
`

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mq.from.M`
    flex-direction: row;
    gap: 48px;
  `}
`

const CardWrapper = styled.div<{ isSmall: boolean }>`
  width: 100%;

  ${mq.from.M`
    ${({ isSmall }: { isSmall: boolean }) =>
      isSmall
        ? `
      flex: 0 0 411px;
      width: 411px;
    `
        : `
      flex: 1 0 0;
      min-width: 411px;
    `}
  `}
`

const SlicesSection = styled(ContentWrapper)`
  display: flex;
  flex-direction: column;
  gap: 80px;
  padding-top: 80px;
  padding-bottom: 80px;
`
